import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from "@mui/material";
import { LazyLoadImage } from "react-lazy-load-image-component";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useUser } from "hooks/useUser";
import { EventsAPI } from "api_inbusiness";
import Tooltip from "lib/components/Tooltip/TooltipWrapper";
import ConfirmModal from "lib/components/Modals/ConfirmModal";
import AddEvent from "pages/Events/AddEvent";
import EventEmail from "pages/Events/EventEmail";
import SeeParticipantsModal from "lib/components/Timeline/TimelineItem/SeeParticipantsModal";
import styles from "./EventCard.module.scss";

const EventCard = ({
  id,
  title,
  subtitle,
  date,
  startTime,
  location,
  county,
  participants,
  tickets,
  custom,
  customId,
  draft,
  description,
  isPast,
  onRefresh,
}) => {
  const navigate = useHistory();
  const { permissionsData } = useUser();
  
  // Modal states
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  const [openParticipants, setOpenParticipants] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [customPassword, setCustomPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  
  // Image cache-buster for refresh after edit
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());

  // Image from API - uses separate /event-images/ endpoint with cache-buster
  const imageUrl = EventsAPI.getImageUrl(id, imageTimestamp);

  // Format date
  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    return dayjs(dateValue).format("DD MMM YYYY");
  };

  // Format time
  const formatTime = (timeValue) => {
    if (!timeValue) return "";
    const newDate = new Date(timeValue);
    const options = { hour12: false, timeZone: "Europe/Bucharest" };
    return newDate.toLocaleTimeString("ro-RO", options).slice(0, 5);
  };

  // Get participant count
  const getParticipantCount = () => {
    if (custom) {
      return tickets?.filter((ticket) => ticket.valid === true).length || 0;
    }
    return participants?.length || 0;
  };

  // Handle card click (open event in new tab)
  const handleCardClick = (e) => {
    // Don't navigate if clicking on action buttons
    if (e.target.closest(`.${styles.actionsBar}`)) return;
    
    if (custom) {
      // For custom events, show password modal
      setOpenPasswordModal(true);
      setCustomPassword("");
      setPasswordError("");
    } else {
      window.open(`/events/${id}`, "_blank");
    }
  };

  // Handle password submit for custom events
  const handlePasswordSubmit = () => {
    if (customPassword === customId) {
      // Password correct - open event
      window.open(`/events/${id}`, "_blank");
      setOpenPasswordModal(false);
      setCustomPassword("");
      setPasswordError("");
    } else {
      setPasswordError("Parola incorectă. Încearcă din nou.");
    }
  };

  // Handle delete
  const handleDelete = () => {
    EventsAPI.delete(id).then((res) => {
      if (res.ok) {
        toast.success("Evenimentul a fost șters cu succes!");
        setOpenConfirmDelete(false);
        onRefresh?.();
      } else {
        toast.error("A intervenit o eroare. Evenimentul nu a putut fi șters");
      }
    });
  };

  // Handle duplicate
  const handleDuplicate = () => {
    EventsAPI.duplicate(id).then((res) => {
      if (res.ok) {
        toast.success("Evenimentul a fost duplicat cu succes! Verifică lista de evenimente.");
        onRefresh?.();
      } else {
        toast.error("A intervenit o eroare. Evenimentul nu a putut fi duplicat.");
      }
    });
  };

  // Selected event data for modals
  const selectedEvent = {
    id,
    title,
    date,
    location,
    description,
    startTime,
    county,
    draft,
    customId,
    custom,
    subtitle,
    tickets,
  };



  return (
    <>
      <div 
        className={`${styles.card} ${custom ? styles.customEvent : ""} ${draft ? styles.draftEvent : ""}`}
        onClick={handleCardClick}
      >
        {/* Image Section */}
        <div className={styles.imageContainer}>
          <LazyLoadImage
            src={imageUrl}
            alt={title}
            effect="blur"
            className={styles.image}
            wrapperClassName={styles.imageWrapper}
          />
          
          {/* Draft Badge */}
          {draft && (
            <div className={styles.draftBadge}>
              Draft
            </div>
          )}
          
          {/* Past Event Badge */}
          {isPast && !draft && (
            <div className={styles.pastBadge}>
              Eveniment încheiat
            </div>
          )}
          
          {/* Private Event Lock Icon */}
          {custom && (
            <div className={styles.lockBadge}>
              <LockIcon fontSize="small" />
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className={styles.content}>
          {/* Title */}
          <Typography 
            variant="h6" 
            className={styles.title}
            title={title}
          >
            {title}
          </Typography>

          {/* Subtitle */}
          {subtitle && (
            <Typography 
              variant="body2" 
              className={styles.subtitle}
            >
              {subtitle}
            </Typography>
          )}

          {/* Event Info */}
          <div className={styles.infoSection}>
            {/* Date & Time */}
            <div className={styles.infoRow}>
              <CalendarTodayIcon className={styles.icon} />
              <Typography variant="body2" className={styles.infoText}>
                {formatDate(date)} {startTime && `• ${formatTime(startTime)}`}
              </Typography>
            </div>

            {/* Location */}
            <div className={styles.infoRow}>
              <LocationOnIcon className={styles.icon} />
              <Typography variant="body2" className={styles.infoText}>
                {county?.name && `${county.name} • `}{location}
              </Typography>
            </div>

            {/* Participants - Admin only */}
            {permissionsData.seeAdminButtons && (
              <div className={styles.infoRow}>
                <PeopleIcon className={styles.icon} />
                <Typography variant="body2" className={styles.infoText}>
                  {getParticipantCount()} participanți
                </Typography>
              </div>
            )}

          </div>

          {/* Admin/Volunteer Actions Bar */}
          {(permissionsData.seeAdminButtons || permissionsData.seeVolunteerSections) && (
            <div className={styles.actionsBar}>
              {/* Scan QR/Ticket - Larger for volunteers */}
              {(permissionsData.seeAdminButtons || permissionsData.seeVolunteerSections) && (
                <Tooltip position="top" textTip="Scanează Bilet" followCursor>
                  <div
                    className={`${styles.actionBtn} ${permissionsData.seeVolunteerSections && !permissionsData.seeAdminButtons ? styles.volunteerPresenceBtn : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate.push(`/scan-qr/event/${id}`);
                    }}
                  >
                    <QrCodeScannerIcon fontSize={permissionsData.seeVolunteerSections && !permissionsData.seeAdminButtons ? "large" : "small"} />
                    {permissionsData.seeVolunteerSections && !permissionsData.seeAdminButtons && (
                      <span className={styles.volunteerBtnText}>Scanează Bilet</span>
                    )}
                  </div>
                </Tooltip>
              )}

              {/* Email - Admin only */}
              {permissionsData.seeAdminButtons && !custom && (
                <Tooltip position="top" textTip="Trimite email" followCursor>
                  <div
                    className={`${styles.actionBtn} ${styles.emailBtn}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenEmail(true);
                    }}
                  >
                    <EmailIcon fontSize="small" />
                  </div>
                </Tooltip>
              )}

              {/* Edit - Admin only */}
              {permissionsData.seeAdminButtons && (
                <Tooltip position="top" textTip="Modifică" followCursor>
                  <div
                    className={`${styles.actionBtn} ${styles.editBtn}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenEditForm(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </div>
                </Tooltip>
              )}

              {/* Participants - Admin only */}
              {permissionsData.seeAdminButtons && !custom && (
                <Tooltip position="top" textTip="Participanți" followCursor>
                  <div
                    className={styles.actionBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenParticipants(true);
                    }}
                  >
                    <BusinessCenterIcon fontSize="small" />
                  </div>
                </Tooltip>
              )}

              {/* Duplicate - Admin only */}
              {permissionsData.seeAdminButtons && (
                <Tooltip position="top" textTip="Duplică" followCursor>
                  <div
                    className={`${styles.actionBtn} ${styles.duplicateBtn}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicate();
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </div>
                </Tooltip>
              )}

              {permissionsData.seeAdminButtons && (
                <Tooltip position="top" textTip="Șterge" followCursor>
                  <div
                    className={`${styles.actionBtn} ${styles.deleteBtn}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenConfirmDelete(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </div>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddEvent
        open={openEditForm}
        setOpen={setOpenEditForm}
        isEditMode={true}
        selectedEvent={selectedEvent}
        getAllEvents={() => {
          setImageTimestamp(Date.now()); // Force image refresh
          if (onRefresh) onRefresh();
        }}
      />

      <EventEmail
        open={openEmail}
        setOpen={setOpenEmail}
        title={title}
        selectedEvent={selectedEvent}
      />

      <SeeParticipantsModal
        open={openParticipants}
        setOpen={setOpenParticipants}
        title={title}
        id={id}
        eventDate={date}
      />

      <ConfirmModal
        open={openConfirmDelete}
        setOpen={setOpenConfirmDelete}
        onClickButtonYes={handleDelete}
        text="Sunteți sigur că doriți să ștergeți acest eveniment?"
      />

      {/* Password Modal for Custom Events */}
      <Dialog 
        open={openPasswordModal} 
        onClose={() => setOpenPasswordModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          🔒 Eveniment cu acces restricționat
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Acest eveniment necesită o parolă pentru accesare.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Parolă"
            value={customPassword}
            onChange={(e) => {
              setCustomPassword(e.target.value);
              setPasswordError("");
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") handlePasswordSubmit();
            }}
            error={!!passwordError}
            helperText={passwordError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordModal(false)}>
            Anulează
          </Button>
          <Button variant="contained" onClick={handlePasswordSubmit}>
            Accesează
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventCard;
